import { __awaiter } from "tslib";
import { DataFrame, DataObject, MemoryDataService, RemoteService, } from '@openhps/core';
import { getStringNoLocale, getThing, saveSolidDatasetAt, createContainerAt, isContainer, deleteSolidDataset, setStringNoLocale, setThing, createSolidDataset, getPodUrlAll, deleteContainer, universalAccess, getSolidDataset, getContainedResourceUrlAll, deleteFile, saveSolidDatasetInContainer, setAgentDefaultAccess, getResourceAcl, getSolidDatasetWithAcl, hasAcl, saveAclFor, setAgentResourceAccess, setGroupDefaultAccess, setGroupResourceAccess, createAcl, setPublicResourceAccess, setPublicDefaultAccess, } from '@inrupt/solid-client';
import { fetch } from 'cross-fetch';
import { vcard, Store, foaf, RDFSerializer, createChangeLog, } from '@openhps/rdf';
import { StorageUtility } from '@inrupt/solid-client-authn-core';
import { ClientRegistrar } from './ClientRegistrar';
import { SubscriptionClient } from '@solid-notifications/subscription';
import { ChannelType } from '@solid-notifications/types';
import { DatasetSubscription } from './DatasetSubscription';
import { DiscoveryClient } from '@solid-notifications/discovery';
class StorageUtilityWrapper extends StorageUtility {
    constructor(secureStorage, insecureStorage) {
        super(secureStorage, insecureStorage);
    }
}
class SolidStorage {
    constructor(driver) {
        this.driver = driver;
    }
    /**
     * Get a key value
     * @param {string} key Key of the key value pair
     * @returns {Promise<string | undefined>} Value
     */
    get(key) {
        return new Promise((resolve) => {
            this.driver
                .findByUID(key)
                .then((s) => {
                resolve(s);
            })
                .catch(() => {
                resolve(undefined);
            });
        });
    }
    /**
     * Set a key vlaue
     * @param {string} key Key of the key value pair
     * @param {string} value Value to store
     * @returns {Promise<void>} Store promise
     */
    set(key, value) {
        return new Promise((resolve, reject) => {
            this.driver
                .insert(key, value)
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    /**
     * Delete an object
     * @param {string} key Key of the key value pair
     * @returns {Promise<void>} Store promise
     */
    delete(key) {
        return new Promise((resolve) => {
            this.driver
                .delete(key)
                .then(resolve)
                .catch(() => resolve(undefined));
        });
    }
}
export class SolidService extends RemoteService {
    constructor(options) {
        super();
        this.options = options || {};
        // Storage
        this.driver = this.options.dataServiceDriver || new MemoryDataService(String);
        this.storage = new SolidStorage(this.driver);
        this.storageUtility = new StorageUtilityWrapper(this.storage, this.storage);
        this.clientRegistrar = new ClientRegistrar(this.storageUtility);
        this.uid = this.constructor.name;
        this.options.defaultOidcIssuer = this.options.defaultOidcIssuer || 'https://login.inrupt.com/';
        this.once('build', () => {
            return this.driver.emitAsync('build');
        });
    }
    get session() {
        return this._session;
    }
    set session(value) {
        this._session = value;
    }
    get clientAuthentication() {
        if (this._clientAuthentication) {
            return this._clientAuthentication;
        }
        const dummy = this.createSession({
            sessionInfo: {
                isLoggedIn: false,
                sessionId: 'dummy',
            },
            storage: this.storage,
            insecureStorage: this.storage,
            secureStorage: this.storage,
        });
        this._clientAuthentication = dummy.clientAuthentication;
        return this._clientAuthentication;
    }
    /**
     * Get the URL of a document
     * @param {SolidSession}    session Solid session to get an URL from
     * @param {string}          [path]  Path to append to the document URL
     * @returns {URL}                    Document URL
     */
    getDocumentURL(session, path) {
        return __awaiter(this, void 0, void 0, function* () {
            const podURL = new URL(yield this.getPodUrl(session));
            const webIdURL = new URL(session.info.webId);
            webIdURL.pathname = '';
            webIdURL.hash = '';
            const documentURL = new URL(podURL.href);
            if (path) {
                const normalizedPath = path.replace(podURL.href, '').replace(webIdURL.href, '');
                const pathURL = new URL((podURL.pathname.length > 1 ? podURL.pathname : '') + normalizedPath.replace(/^\//, ''), podURL.href);
                documentURL.pathname = pathURL.pathname;
                documentURL.hash = pathURL.hash;
            }
            return documentURL;
        });
    }
    getPodUrl(session) {
        return new Promise((resolve, reject) => {
            getPodUrlAll(session.info.webId, {
                fetch: session.fetch,
            })
                .then((value) => {
                // No storage
                if (value[0] === undefined) {
                    // Get the POD url based on the web id by removing /profile/card#me
                    const webIdURL = new URL(session.info.webId);
                    webIdURL.hash = '';
                    webIdURL.pathname = webIdURL.pathname.replace(/\/profile\/card$/, '/');
                    resolve(webIdURL.href);
                    return;
                }
                resolve(value[0]);
            })
                .catch(reject);
        });
    }
    /**
     * Get a Solid dataset as an N3 Quads dataset
     * @param {SolidSession} session Solid session to get a thing from
     * @param {string} uri URI of the thing in the Solid Pod
     * @returns {Promise<Store & RDFChangeLog>} Promise of a solid dataset store
     */
    getDatasetStore(session, uri) {
        return new Promise((resolve, reject) => {
            this.getDataset(session, uri)
                .then((dataset) => {
                if (!dataset) {
                    dataset = createSolidDataset();
                }
                const quads = Object.keys(dataset.graphs)
                    .map((key) => {
                    const graph = dataset.graphs[key];
                    return RDFSerializer.subjectsToQuads(Object.values(graph));
                })
                    .reduce((a, b) => a.concat(b), []);
                const store = createChangeLog(new Store(quads));
                const result = Object.assign(store, dataset);
                resolve(result);
            })
                .catch(reject);
        });
    }
    createDataset() {
        return createSolidDataset();
    }
    /**
     * Get a Solid dataset
     * @param {SolidSession} session Solid session to get a thing from
     * @param {string} uri URI of the thing in the Solid Pod
     * @returns {Promise<SolidDataset>} Promise of a solid dataset
     */
    getDataset(session, uri) {
        return new Promise((resolve, reject) => __awaiter(this, void 0, void 0, function* () {
            const documentURL = uri.startsWith('http') ? new URL(uri) : yield this.getDocumentURL(session, uri);
            documentURL.hash = '';
            getSolidDataset(documentURL.href, session ? { fetch: session.fetch } : this.session)
                .then(resolve)
                .catch((ex) => {
                if (ex.response.status === 404) {
                    // Create dataset when 404 (not found)
                    resolve(undefined);
                }
                else {
                    reject(ex);
                }
            });
        }));
    }
    /**
     * Get a Solid dataset subscription
     * @param {SolidSession} session Solid session to get a subscription from
     * @param {string} uri URI of the thing in the Solid Pod
     * @returns {Promise<DatasetSubscription>} Promise of a solid dataset subscription
     */
    getDatasetSubscription(session, uri) {
        const fetchFn = session ? session.fetch : fetch;
        /**
         *
         */
        function legacySubscription() {
            return new Promise((resolve, reject) => {
                fetchFn(uri, {
                    method: 'HEAD',
                })
                    .then((response) => {
                    const websocketUri = response.headers.get('updates-via');
                    if (!websocketUri) {
                        return resolve(undefined);
                    }
                    resolve(websocketUri);
                })
                    .catch(reject);
            });
        }
        /**
         *
         */
        function newSubscription() {
            return new Promise((resolve, reject) => {
                const discovery = new DiscoveryClient(fetchFn);
                Promise.all([discovery.findService(uri, ChannelType.WebSocketChannel2023)])
                    .then(([ws]) => {
                    if (!ws) {
                        reject(new Error('No WebSocket service found'));
                        return;
                    }
                    const client = new SubscriptionClient(fetchFn);
                    return client.subscribe(uri, ws.channelType);
                })
                    .then((service) => {
                    resolve(service.receiveFrom);
                })
                    .catch(reject);
            });
        }
        /**
         *
         * @param uri
         * @param channelType
         */
        function createSubscription(uri, channelType) {
            return new Promise((resolve, reject) => {
                DatasetSubscription.create(uri, channelType)
                    .then((subscription) => {
                    subscription.subscribe(uri);
                    resolve(subscription);
                })
                    .catch(reject);
            });
        }
        return new Promise((resolve, reject) => {
            legacySubscription()
                .then((uri) => {
                if (uri) {
                    createSubscription(uri, undefined).then(resolve).catch(reject);
                }
                else {
                    newSubscription()
                        .then((uri) => {
                        return createSubscription(uri, ChannelType.WebSocketChannel2023);
                    })
                        .then(resolve)
                        .catch(reject);
                }
            })
                .catch(reject);
        });
    }
    /**
     * Create a Solid container
     * @param session Solid session to create a container with
     * @param url URL of the container
     * @returns Promise of the container
     */
    createContainer(session, url) {
        return new Promise((resolve, reject) => {
            // Ensure url ends with /
            if (!url.endsWith('/')) {
                url += '/';
            }
            // First check if the container does not exist yet
            this.getDataset(session, url)
                .then((dataset) => {
                if (dataset && isContainer(dataset)) {
                    resolve(dataset);
                    return;
                }
                else if (dataset && !isContainer(dataset)) {
                    reject(new Error('Resource already exists and is not a container'));
                    return;
                }
                // Create container (can still fail based on permissions)
                return createContainerAt(url, session
                    ? {
                        fetch: session.fetch,
                    }
                    : undefined);
            })
                .then(resolve)
                .catch(reject);
        });
    }
    deleteContainer(session, url) {
        return new Promise((resolve, reject) => {
            deleteContainer(url, session
                ? {
                    fetch: session.fetch,
                }
                : undefined)
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    deleteRecursively(session, dataset) {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof dataset === 'string') {
                const fetchedDataset = yield this.getDataset(session, dataset);
                if (!fetchedDataset) {
                    return Promise.resolve();
                }
                return yield this.deleteRecursively(session, fetchedDataset);
            }
            if (!dataset) {
                return Promise.resolve();
            }
            const containedResourceUrls = getContainedResourceUrlAll(dataset);
            const containedDatasets = yield Promise.all(containedResourceUrls.map((resourceUrl) => __awaiter(this, void 0, void 0, function* () {
                try {
                    return yield getSolidDataset(resourceUrl, session ? { fetch: session.fetch } : this.session);
                }
                catch (e) {
                    // The Resource might not have been a SolidDataset;
                    // we can delete it directly:
                    yield deleteFile(resourceUrl, session ? { fetch: session.fetch } : this.session);
                    return null;
                }
            })));
            yield Promise.all(containedDatasets.map((containedDataset) => __awaiter(this, void 0, void 0, function* () {
                if (containedDataset === null) {
                    return;
                }
                return yield this.deleteRecursively(session, containedDataset);
            })));
            return yield deleteSolidDataset(dataset, session ? { fetch: session.fetch } : this.session);
        });
    }
    /**
     * Delete a Solid dataset
     * @param {SolidSession} session Solid session to get a thing from
     * @param {string} uri URI of the thing in the Solid Pod
     * @returns {Promise<SolidDataset>} Promise of a solid dataset
     */
    deleteDataset(session, uri) {
        return new Promise((resolve, reject) => {
            deleteSolidDataset(uri, session ? { fetch: session.fetch } : this.session)
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    /**
     * Save a Solid dataset
     * @param {SolidSession} session Solid session to get a thing from
     * @param {string} uri URI of the thing in the Solid Pod
     * @param {SolidDataset | (Store & RDFChangeLog)} dataset Dataset to save at the uri
     * @param {boolean} [append] Append in container
     * @returns {Promise<SolidDataset | null>} Promise of a solid dataset
     */
    saveDataset(session, uri, dataset, append) {
        return new Promise((resolve, reject) => {
            var _a, _b;
            const options = session ? { fetch: session.fetch } : this.session;
            const documentURL = new URL(uri);
            documentURL.hash = '';
            const containerURL = new URL(documentURL.href);
            // Remove path resource so it ends with a /
            containerURL.pathname = containerURL.pathname.replace(/\/[^/]+$/, '/');
            if (dataset instanceof Store) {
                let additions = [];
                let deletions = [];
                if (dataset['additions'] && dataset['deletions']) {
                    additions = (_a = dataset.additions) !== null && _a !== void 0 ? _a : [];
                    deletions = (_b = dataset.deletions) !== null && _b !== void 0 ? _b : [];
                }
                else {
                    dataset = createChangeLog(dataset);
                    // Add all quads to the additions
                    additions = dataset.getQuads(null, null, null, null);
                }
                if (additions.length === 0 && deletions.length === 0) {
                    resolve(null);
                    return;
                }
                const internalDataset = (dataset.type === 'Dataset' ? dataset : createSolidDataset());
                const dummyDataset = Object.assign(Object.assign({}, this.storeToDataset(dataset)), { internal_changeLog: {
                        additions,
                        deletions,
                    }, internal_resourceInfo: internalDataset.internal_resourceInfo });
                dataset = dummyDataset;
            }
            else {
                dataset = dataset ? dataset : createSolidDataset();
            }
            if (append) {
                // Add a hint to the dataset about the URI it should be saved at
                const resourceInfo = Object.assign({ sourceIri: documentURL.href }, dataset.internal_resourceInfo);
                dataset = Object.assign(Object.assign({}, dataset), { internal_resourceInfo: resourceInfo });
                saveSolidDatasetInContainer(containerURL.href, dataset, options).then(resolve).catch(reject);
            }
            else {
                saveSolidDatasetAt(documentURL.href, dataset, options).then(resolve).catch(reject);
            }
        });
    }
    /**
     * Get a thing from a session Pod
     * @param {SolidSession} session Solid session to get a thing from
     * @param {string} uri URI of the thing in the Solid Pod
     * @returns {Promise<Thing>} Persisted thing
     */
    getThing(session, uri) {
        return new Promise((resolve, reject) => {
            this.getDataset(session, uri)
                .then((dataset) => {
                const thing = getThing(dataset, uri);
                resolve(thing);
            })
                .catch(reject);
        });
    }
    /**
     * Set access control list for a specific object
     * @param {IriString} uri URI of the object
     * @param {Partial<AccessModes & { default?: boolean, public?: boolean, group?: boolean }>} access Access modes
     * @param {string} [webId] WebID to set access for
     * @param {SolidSession} [session] Session to use
     */
    setAccess(uri, access, webId = foaf.Agent, session) {
        return new Promise((resolve, reject) => {
            session = session !== null && session !== void 0 ? session : this.session;
            getSolidDatasetWithAcl(uri, session ? { fetch: session.fetch } : undefined)
                .then((dataset) => {
                let acl;
                // Get or access Acl
                if (!hasAcl(dataset)) {
                    acl = getResourceAcl(dataset);
                }
                else {
                    acl = createAcl(dataset);
                    // Set owner permissions
                    acl = setAgentDefaultAccess(acl, session.info.webId, {
                        read: true,
                        append: true,
                        write: true,
                        control: true,
                    });
                    acl = setAgentResourceAccess(acl, session.info.webId, {
                        read: true,
                        append: true,
                        write: true,
                        control: true,
                    });
                }
                access.group = access.group || webId === foaf.Agent || webId === 'public';
                // Resource access
                if (access.group) {
                    acl = setGroupResourceAccess(acl, webId, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                else {
                    acl = setAgentResourceAccess(acl, webId, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                // Set public access
                if (access.public) {
                    acl = setPublicResourceAccess(acl, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                if (access.public && access.default) {
                    acl = setPublicDefaultAccess(acl, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                // Set default group access
                if (access.group && access.default) {
                    acl = setGroupDefaultAccess(acl, webId, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                else if (access.default) {
                    acl = setAgentDefaultAccess(acl, webId, {
                        read: access.read,
                        append: access.append,
                        write: access.write,
                        control: access.controlWrite,
                    });
                }
                return saveAclFor(dataset, acl, session ? { fetch: session.fetch } : this.session);
            })
                .then(() => {
                resolve();
            })
                .catch(reject);
        });
    }
    /**
     * Get access control list for a specific resource
     * @param {SolidSession} session Session to use
     * @param uri
     * @param webId WebID to get access control list for
     * @returns Access control list
     */
    getAccess(uri, webId = foaf.Agent, session) {
        return new Promise((resolve, reject) => {
            universalAccess
                .getAgentAccess(uri, webId, session
                ? {
                    fetch: session.fetch,
                }
                : undefined)
                .then((access) => {
                resolve(access);
            })
                .catch(reject);
        });
    }
    /**
     * Set a thing in a session Pod
     * @param {SolidSession} session Solid session to set a thing to
     * @param {ThingPersisted} thing Persisted thing to store in the Pod
     * @returns {Promise<SolidDataset>} Promise if stored
     */
    setThing(session, thing) {
        return this.createThing(session, thing);
    }
    /**
     * Set a thing in a session Pod
     * @param {SolidSession} session Solid session to set a thing to
     * @param {Thing} thing Non-persisted thing to store in the Pod
     * @param {SolidDataset} [dataset]
     * @returns {Promise<SolidDataset>} Promise if stored
     */
    createThing(session, thing, dataset) {
        return new Promise((resolve, reject) => {
            const documentURL = new URL(thing.url);
            documentURL.hash = '';
            /**
             *
             * @param dataset
             * @param persist
             */
            function setThingInDataset(dataset, persist = true) {
                var _a;
                const existingThing = (_a = getThing(dataset, thing.url)) !== null && _a !== void 0 ? _a : {};
                const newThing = this._mergeDeep(existingThing, thing);
                dataset = setThing(dataset, newThing);
                if (persist) {
                    this.saveDataset(session, dataset, documentURL.href)
                        .then(() => {
                        resolve(dataset);
                    })
                        .catch(reject);
                }
                else {
                    resolve(dataset);
                }
            }
            if (dataset) {
                setThingInDataset.bind(this)(dataset, false);
            }
            else {
                this.getDataset(session, documentURL.href)
                    .then((dataset) => {
                    if (!dataset) {
                        dataset = createSolidDataset();
                    }
                    setThingInDataset.bind(this)(dataset);
                })
                    .catch(reject);
            }
        });
    }
    /**
     * Check if something is an object
     * @param {any} item Item to check for object
     * @returns {boolean} Is an object
     */
    _isObject(item) {
        return item && typeof item === 'object' && !Array.isArray(item);
    }
    /**
     * Deep merge objects
     * @param {any} target Target object
     * @param {any} source Source object
     * @returns {any} Merged object
     */
    _mergeDeep(target, source) {
        const output = Object.assign({}, target);
        if (this._isObject(target) && this._isObject(source)) {
            Object.keys(source).forEach((key) => {
                if (Array.isArray(source[key])) {
                    output[key] = source[key];
                    output[key].push(...(target[key] || []));
                }
                else if (this._isObject(source[key])) {
                    if (!(key in target))
                        Object.assign(output, { [key]: source[key] });
                    else
                        output[key] = this._mergeDeep(target[key], source[key]);
                }
                else {
                    Object.assign(output, { [key]: source[key] });
                }
            });
        }
        return output;
    }
    /**
     * Send a push to a specific remote node
     * @param {string} uid Remote Node UID
     * @param {DataFrame} frame Data frame to push
     * @param {PushOptions} [options] Push options
     */
    remotePush(uid, frame, options) {
        return new Promise((resolve, reject) => {
            resolve(undefined);
        });
    }
    /**
     * Send a pull request to a specific remote node
     * @param {string} uid Remote Node UID
     * @param {PullOptions} [options] Pull options
     */
    remotePull(uid, options) {
        return new Promise((resolve, reject) => {
            resolve(undefined);
        });
    }
    /**
     * Send an error to a remote node
     * @param {string} uid Remote Node UID
     * @param {string} event Event to send
     * @param {any[]} [args] Event argument
     */
    remoteEvent(uid, event, ...args) {
        return new Promise((resolve, reject) => {
            resolve(undefined);
        });
    }
    /**
     * Send a remote service call
     * @param {string} uid Service uid
     * @param {string} method Method to call
     * @param {any[]} [args] Optional set of arguments
     */
    remoteServiceCall(uid, method, ...args) {
        return new Promise((resolve, reject) => {
            resolve(undefined);
        });
    }
    /**
     * Fetch profile information
     * @param {SolidProfileObject} object Data object to fetch profile information for
     * @returns {Promise<SolidProfileObject>} Promise of data object with profile information
     */
    fetchProfile(object) {
        return new Promise((resolve, reject) => {
            this.findSessionByWebId(object.webId)
                .then((session) => {
                return getSolidDataset(object.profileDocumentUrl.href, session
                    ? {
                        fetch: session.fetch,
                    }
                    : undefined);
            })
                .then((dataset) => {
                const profile = getThing(dataset, object.webId);
                object.displayName = getStringNoLocale(profile, vcard.fn);
                resolve(object);
            })
                .catch(reject);
        });
    }
    /**
     * Store profile information
     * @param {SolidProfileObject} object Data object to store profile information for
     * @returns {Promise<void>} Promise of storing
     */
    storeProfile(object) {
        return new Promise((resolve, reject) => {
            const key = `${SolidService.PREFIX}:webId:${object.webId}`;
            this.storage
                .set(key, object.sessionId)
                .then(() => {
                return this.findSessionByWebId(object.webId);
            })
                .then((session) => {
                const profileDocumentUrl = object.profileDocumentUrl;
                if (!profileDocumentUrl) {
                    resolve(undefined);
                    return;
                }
                return getSolidDataset(profileDocumentUrl.href, session
                    ? {
                        fetch: session.fetch,
                    }
                    : undefined);
            })
                .then((dataset) => {
                const profile = getThing(dataset, object.webId);
                setStringNoLocale(profile, vcard.fn, object.displayName);
                resolve(object);
            })
                .catch(reject);
        });
    }
    /**
     * Find session information by session identifier
     * @param {string} sessionId Session identifier
     * @returns {ISessionInfo & ISessionInternalInfo} Session info
     */
    findSessionInfoById(sessionId) {
        return new Promise((resolve, reject) => {
            // First check in memory
            this.clientAuthentication
                .getSessionInfo(sessionId)
                .then((sessionInfo) => {
                resolve(sessionInfo);
            })
                .catch(reject);
        });
    }
    /**
     * Find session by session identifier
     * @param {string} sessionId Session identifier
     * @returns {SolidSession} Browser or Node session
     */
    findSessionById(sessionId) {
        return new Promise((resolve, reject) => {
            let session = undefined;
            this.findSessionInfoById(sessionId)
                .then((sessionInfo) => {
                if (sessionInfo === undefined) {
                    resolve(undefined);
                    return;
                }
                session = this.createSession({
                    sessionInfo,
                    storage: this.storage,
                    insecureStorage: this.storage,
                    secureStorage: this.storage,
                });
                // Validate session
                if (!sessionInfo.isLoggedIn && sessionInfo.issuer) {
                    return session.login({
                        oidcIssuer: sessionInfo.issuer,
                        clientId: sessionInfo.clientAppId,
                        clientSecret: sessionInfo.clientAppSecret,
                        handleRedirect: () => { },
                    });
                }
            })
                .then(() => {
                resolve(session);
            })
                .catch(reject);
        });
    }
    /**
     * Find session by WebID
     * @param {string} webId Web Identifier
     * @returns {SolidSession} Browser or Node session
     */
    findSessionByWebId(webId) {
        return new Promise((resolve, reject) => {
            const key = `${SolidService.PREFIX}:webId:${webId}`;
            this.storage
                .get(key)
                .then((sessionId) => {
                if (!sessionId) {
                    resolve(undefined);
                    return;
                }
                return this.findSessionById(sessionId);
            })
                .then((session) => {
                resolve(session);
            })
                .catch(reject);
        });
    }
    /**
     * Find session by object identifier
     * @param {Constructor<DataObject | DataFrame>} dataType Data type of data object or data frame
     * @param {string} uid Object unique identifier
     * @returns {SolidSession} Browser or Node session
     */
    findSessionByObjectUID(dataType, uid) {
        return new Promise((resolve, reject) => {
            const prefix = `${SolidService.PREFIX}:${dataType.name}`;
            const key = `${prefix}:${uid}`;
            this.storage
                .get(key)
                .then((sessionId) => {
                return this.findSessionById(sessionId);
            })
                .then(resolve)
                .catch(reject);
        });
    }
    linkSession(object, sessionId, type) {
        return new Promise((resolve, reject) => {
            const constructor = type !== null && type !== void 0 ? type : object.constructor;
            const prefix = `${SolidService.PREFIX}:${constructor.name}`;
            const key = `${prefix}:${object.uid}`;
            this.storage.set(key, sessionId).then(resolve).catch(reject);
        });
    }
    unlinkSession(object, type) {
        return new Promise((resolve, reject) => {
            const constructor = type !== null && type !== void 0 ? type : object.constructor;
            const prefix = `${SolidService.PREFIX}:${constructor.name}`;
            const key = `${prefix}:${object instanceof DataObject || object instanceof DataFrame ? object.uid : object}`;
            this.storage.delete(key).then(resolve).catch(reject);
        });
    }
    storeToDataset(store) {
        const dataset = {
            graphs: {
                default: {},
            },
            type: 'Dataset',
        };
        const subjects = store.getSubjects(undefined, undefined, undefined);
        subjects
            .filter((s) => s.termType !== 'BlankNode')
            .forEach((subject) => {
            const thing = RDFSerializer.quadsToThing(subject, store);
            const subjects = RDFSerializer.thingToSubjects(thing);
            subjects.forEach((subject) => {
                dataset.graphs.default[subject.url] = subject;
            });
        });
        return dataset;
    }
}
SolidService.PREFIX = 'OpenHPS:solid';
(() => {
    // Register context for 'https://www.w3.org/ns/solid/notification/v1'
    RDFSerializer.getDocumentLoader().addContext('https://www.w3.org/ns/solid/notification/v1', {});
})();
//# sourceMappingURL=SolidService.js.map